import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { auth } from "../../utilities/firebase";
import { getAdmin } from "../api/admin";
import { getPublishingHouse } from "../api/publishingHouse";
import {
  SUPERADMIN_ROLE,
  type Admin,
  type PublishingHouse,
} from "../../types/database";

export type AuthStatus = "loading" | "anon" | "not-admin" | "admin";

interface AuthContextValue {
  firebaseUser: User | null;
  admin: Admin | null;
  publishingHouse: PublishingHouse | null;
  status: AuthStatus;
  isSuperadmin: boolean;
  logout: () => Promise<void>;
  refreshPublishingHouse: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [publishingHouse, setPublishingHouse] =
    useState<PublishingHouse | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setFirebaseUser(null);
        setAdmin(null);
        setPublishingHouse(null);
        setStatus("anon");
        return;
      }
      setFirebaseUser(user);
      setStatus("loading");
      try {
        // 401/403 here means the user has no admins/{uid} record → not a publisher.
        const adminDoc = await getAdmin(user.uid);
        setAdmin(adminDoc);
        // best-effort: resolve the driven publishing house for the shell.
        try {
          setPublishingHouse(
            await getPublishingHouse(adminDoc.publishingHouseId),
          );
        } catch {
          setPublishingHouse(null);
        }
        setStatus("admin");
      } catch {
        setAdmin(null);
        setPublishingHouse(null);
        setStatus("not-admin");
      }
    });
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  const refreshPublishingHouse = async () => {
    if (!admin?.publishingHouseId) return;
    try {
      setPublishingHouse(await getPublishingHouse(admin.publishingHouseId));
    } catch {
      /* keep current value */
    }
  };

  const value: AuthContextValue = {
    firebaseUser,
    admin,
    publishingHouse,
    status,
    isSuperadmin: admin?.role === SUPERADMIN_ROLE,
    logout,
    refreshPublishingHouse,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
